import FetcherFactory from 'Shared/FetcherFactory'
import { getAuthenticityToken } from 'Shared/helpers'

export const reducerName = 'SESSION_SAVER'

/**
 * {
 *   id: CLONE_LEAGUE_ID,
 *   cloningStep: 2,
 *   leagues: {
 *    'SOURCE_LEAGUE_ID_1': {
 *      'new_name': 'My Cool League',
 *      'should_clone_rounds': true,
 *      'new_round_dates': [...],
 *      'should_clone_players': false,
 *    },
 *    'SOURCE_LEAGUE_ID_2': {
 *      'new_name': 'Weekend Warriors'
 *      // Similar to above
 *    }
 *   }
 * }
 */
const factory = new FetcherFactory(
  reducerName,
  ({ params, state }) => {
    const { cloneLeaguesSessionID, data } = params

    const body = {
      clone_leagues_session_id: cloneLeaguesSessionID,
      data: data,
    }

    return fetch(state.baseURL, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'X-CSRF-Token': getAuthenticityToken(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    }).then(response => {
      return response.json().then(data => ({
        data: { cloneLeaguesSession: data},
        status: response.status,
      }))
    })
  },
  {
    ignoreParamsCaching: true,
  },
)

const SessionSaverReducer = factory.getReducer()
export const namedReducer = { [reducerName]: SessionSaverReducer }

export const getInitialState = ({
  cloneLeaguesSession,
  upsertCloneLeaguesSessionURL,
  editCloneLeaguesSessionURL,
  indexCloneLeaguesSessionsURL,
}) => {
  return {
    data: {
      cloneLeaguesSession: cloneLeaguesSession,
    },
    baseURL: upsertCloneLeaguesSessionURL,
    editURL: editCloneLeaguesSessionURL,
    indexURL: indexCloneLeaguesSessionsURL,
  }
}

export const selectCloneLeaguesSession = state => state[factory.name]?.data?.cloneLeaguesSession
export const selectCloneLeaguesSessionID = state => state[factory.name]?.data?.cloneLeaguesSession?.id
export const selectCloneLeaguesEditURLTemplate = state => state[factory.name]?.editURL
export const selectCloneLeaguesIndexURL = state => state[factory.name]?.indexURL
export const selectCloneLeaguesSessionData = state => state[factory.name]?.data?.cloneLeaguesSession?.data
export const selectSessionSaverLoading = state => state[factory.name]?.loading
export const selectSessionSaverError = state => state[factory.name]?.error

export const saveSession = factory.getFetchAction()
export const setSessionSaverData = factory.getSetDataAction()

export const selectAllLeaguesRenamed = (state) => {
  const leagues = selectCloneLeaguesSessionData(state)?.leagues
  if (!leagues) {
    return false
  }

  const entries = Object.values(leagues)
  if (!entries.length) {
    return false
  }

  return entries.every(league =>
    league.league_name && league.league_name !== league.name,
  )
}

export const addLeaguesToSession = (leaguesDict) => (dispatch, getState) => {
  const session = selectCloneLeaguesSession(getState())
  const existingLeagues = session?.data?.leagues || {}

  const newLeagues = Object.entries(leaguesDict).reduce((acc, [ id, data ]) => {
    if (!acc[id]) {
      acc[id] = { ...data }
    }
    return acc
  }, { ...existingLeagues })

  dispatch(setSessionSaverData({
    cloneLeaguesSession: {
      ...session,
      data: { ...session?.data, leagues: newLeagues },
    },
  }))
}

export const removeLeaguesFromSession = (leagueIds) => (dispatch, getState) => {
  const session = selectCloneLeaguesSession(getState())
  const existingLeagues = session?.data?.leagues || {}

  const filtered = Object.keys(existingLeagues)
    .filter(id => !leagueIds.includes(id))
    .reduce((acc, id) => {
      acc[id] = existingLeagues[id]
      return acc
    }, {})

  dispatch(setSessionSaverData({
    cloneLeaguesSession: {
      ...session,
      data: { ...session?.data, leagues: filtered },
    },
  }))
}

export const updateLeagueConfig = (leagueId, configs) => (dispatch, getState) => {
  const session = selectCloneLeaguesSession(getState())
  const existingLeagues = session?.data?.leagues || {}

  dispatch(setSessionSaverData({
    cloneLeaguesSession: {
      ...session,
      data: {
        ...session?.data,
        leagues: {
          ...existingLeagues,
          [leagueId]: { ...existingLeagues[leagueId], ...configs },
        },
      },
    },
  }))
}

export const updateAllLeaguesConfigs = (configs) => (dispatch, getState) => {
  const session = selectCloneLeaguesSession(getState())
  const existingLeagues = session?.data?.leagues || {}

  const updatedLeagues = Object.entries(existingLeagues).reduce((acc, [ id, league ]) => {
    acc[id] = { ...league, ...configs }
    return acc
  }, {})

  // TODO: Check if the configs should be saved globally, not within each league individually
  dispatch(setSessionSaverData({
    cloneLeaguesSession: {
      ...session,
      data: { ...session?.data, leagues: updatedLeagues },
    },
  }))
}
